import { useCallback, useEffect, useState } from "react";
import { Mail, Phone, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import AdminListPagination from "./AdminListPagination";

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  ["next_due", "Next reminder (soonest)"],
  ["name_asc", "Name (A–Z)"],
  ["name_desc", "Name (Z–A)"],
  ["joined_desc", "Joined (newest)"],
  ["joined_asc", "Joined (oldest)"],
  ["reminders_desc", "Most reminders sent"],
  ["reminders_asc", "Fewest reminders sent"],
  ["city", "City (A–Z)"],
  ["category", "Category (A–Z)"],
];

const STATUS_OPTIONS = [
  ["", "All statuses"],
  ["awaiting", "Awaiting KYC"],
  ["not_activated", "Account not activated"],
  ["exhausted", "10 reminders sent"],
];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminArtistKycFollowups({ toast }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("next_due");
  const [status, setStatus] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/artist-kyc-followups", {
        params: { page, limit: pageSize, sort, status },
      });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      toast(formatApiError(error), "error");
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sort, status, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="card" data-testid="admin-artist-kyc-followups">
      <div className="card-head">
        <div>
          <div className="card-title">Artist KYC Follow-ups ({total.toLocaleString("en-IN")})</div>
          <div className="text-muted fs-11">
            Artists who have not submitted KYC. Automated reminders go out every 3 days, up to 10 times.
          </div>
        </div>
        <div className="flex items-center gap-8" style={{ flexWrap: "wrap" }}>
          <select
            className="field-input"
            aria-label="Filter by follow-up status"
            data-testid="kyc-followups-status"
            value={status}
            onChange={(event) => { setStatus(event.target.value); setPage(1); }}
          >
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select
            className="field-input"
            aria-label="Sort artists"
            data-testid="kyc-followups-sort"
            value={sort}
            onChange={(event) => { setSort(event.target.value); setPage(1); }}
          >
            {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>Sort: {label}</option>)}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={reload} disabled={loading}>
            <RefreshCw size={14} aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      <div className="table-responsive">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Artist</th>
              <th>Contact</th>
              <th>Joined</th>
              <th>Reminders</th>
              <th>Next reminder</th>
              <th>Follow-up status</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={6} className="text-muted">Loading artists…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-muted">{status ? "No artists match this status." : "No artists are waiting to submit KYC."}</td></tr>
            ) : items.map((artist) => {
              const subject = encodeURIComponent("Complete your BookTalent artist profile and submit KYC");
              const body = encodeURIComponent(
                `Hi ${artist.name || "there"},\n\nPlease complete your artist profile and submit your KYC details on BookTalent so we can review your application.\n\nOpen your artist dashboard to continue.`
              );
              return (
                <tr key={artist.user_id}>
                  <td>
                    <strong>{artist.stage_name || artist.name}</strong>
                    {(artist.category || artist.city) && (
                      <div className="text-muted fs-11">
                        {[artist.category, artist.city].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td>
                    <div>{artist.email || "No email available"}</div>
                    <div className="flex items-center gap-8 mt-8">
                      {artist.email && (
                        <a className="btn btn-ghost btn-sm" href={`mailto:${artist.email}?subject=${subject}&body=${body}`}>
                          <Mail size={13} aria-hidden="true" /> Email
                        </a>
                      )}
                      {artist.phone && (
                        <a className="btn btn-ghost btn-sm" href={`tel:${artist.phone}`}>
                          <Phone size={13} aria-hidden="true" /> Call
                        </a>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(artist.created_at)}</td>
                  <td>{artist.reminders_sent || 0} / 10</td>
                  <td>{artist.status === "exhausted" ? "—" : formatDate(artist.next_due_at)}</td>
                  <td>
                    <span className={`pill ${artist.status === "exhausted" ? "pill-amber" : "pill-blue"}`}>
                      {artist.status === "exhausted"
                        ? "10 reminders sent"
                        : artist.pending_activation
                          ? "Account not activated"
                          : "Awaiting KYC"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AdminListPagination
        page={page}
        pages={pages}
        total={total}
        pageSize={pageSize}
        onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
        onPageChange={setPage}
        testId="admin-artist-kyc-followups"
        itemLabel="artists"
      />
    </div>
  );
}
